/**
 * Line-item / quote provenance helpers. A value is either entered by hand
 * (`source: 'manual'`) or detected by the assistant from a document
 * (`documentId` set by the extraction job). Kept in a leaf module so the pure
 * logic is unit-testable without the SPA's DOM dependencies (HS2-ZNM6Z8).
 */
export interface Provenance {
  documentId?: string;
  source?: string;
  confidence?: number;
  /** Where a filled-in unit came from (HS2-T1N9FJ): a matched quote, or inferred from the item. */
  unitSource?: 'quote' | 'inferred';
  unitVendor?: string;
  quantitySource?: 'quote';
  quantityVendor?: string;
  /** Vendors quoted different units for this line (kg vs ton): the line is flagged. */
  unitConflict?: string[];
  /** Original extracted line kept because a confirmed quote still points to it after re-extraction. */
  retainedAfterReextract?: boolean;
  /** Extracted line kept because a confirmed quote still points to it after its document changed type. */
  retainedAfterDocumentMove?: boolean;
  /** The original file was deleted; retain its name without a dead document reference. */
  sourceDocumentDeleted?: boolean;
  sourceDocumentName?: string;
}

/** 'manual' | 'extracted' | '' — the coarse origin of a value. */
export function provenanceLabel(p: Provenance | null | undefined): 'manual' | 'extracted' | '' {
  if (p == null) return '';
  if (p.source === 'manual') return 'manual';
  if (p.source === 'extracted') return 'extracted';
  if (typeof p.documentId === 'string') return 'extracted';
  return '';
}

/** True when the value was detected by the assistant (extracted from a document)
 *  rather than entered by hand — drives the robot marker in the UI. */
export function isAiDetected(p: Provenance | null | undefined): boolean {
  return provenanceLabel(p) === 'extracted';
}
